import React, { useEffect, useState } from "react";
import { Waves, Coffee, ParkingSquare, Zap, Wifi, Dumbbell, Bike, ExternalLink, MapPin, Sunset, Anchor, MessageSquare } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { absolutizeUrl } from "@/components/ImagePicker";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";

const WA_NUMBER = "393333378593";

const HOTEL_BOOKING_URL = "https://booking.passepartout.cloud/booking?oidPortale=14405&lingua=it";
const HOTEL_QUOTE_URL = "https://villasmeraldo.com/#prenotazione";
const HOTEL_SITE_URL = "https://www.villasmeraldo.com";

// Defaults — overridable from /admin/stay via the site_content/stay document
const DEFAULTS = {
    hero_image: "https://images.pexels.com/photos/1450363/pexels-photo-1450363.jpeg",
    beach_image: "https://images.pexels.com/photos/1320684/pexels-photo-1320684.jpeg",
    pool_image: "https://images.pexels.com/photos/261101/pexels-photo-261101.jpeg",
    breakfast_image: "https://images.pexels.com/photos/1855214/pexels-photo-1855214.jpeg",
    hero_caption_it: "",
    hero_caption_en: "",
    beach_caption_it: "",
    beach_caption_en: "",
    pool_caption_it: "",
    pool_caption_en: "",
    breakfast_caption_it: "",
    breakfast_caption_en: "",
};

function isAugust() {
    return new Date().getMonth() === 7;
}

function ServiceCard({ icon: Icon, title, desc }) {
    return (
        <div className="bg-zinc-950 border border-white/10 hover:border-abnormal-acid transition-colors p-5 flex flex-col gap-3">
            <Icon size={22} className="text-abnormal-acid" />
            <h3 className="font-display text-xl uppercase leading-tight">{title}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
        </div>
    );
}

function RoomCard({ name, count, desc }) {
    return (
        <div data-testid={`room-${name.toLowerCase().replace(/\s/g, "-")}`} className="border border-white/10 bg-zinc-950 p-5 flex flex-col gap-2 hover:border-abnormal-acid transition-colors">
            <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-xl uppercase leading-tight">{name}</h3>
                <span className="text-[10px] tracking-[0.25em] uppercase text-abnormal-acid font-bold border border-abnormal-acid/40 px-2 py-1">{count}</span>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
        </div>
    );
}

export default function Stay() {
    const { t, lang } = useLang();
    const showAugustBanner = isAugust();
    const r = t("stay.roomsList");
    const svc = t("stay.services");
    const [content, setContent] = useState(DEFAULTS);

    useEffect(() => {
        api.get("/site-content/stay").then((res) => {
            const data = res.data?.data || {};
            setContent({ ...DEFAULTS, ...data });
        }).catch(() => { /* keep defaults */ });
    }, []);

    const heroImg = absolutizeUrl(content.hero_image || DEFAULTS.hero_image);
    const beachImg = absolutizeUrl(content.beach_image || DEFAULTS.beach_image);
    const poolImg = absolutizeUrl(content.pool_image || DEFAULTS.pool_image);
    const breakfastImg = absolutizeUrl(content.breakfast_image || DEFAULTS.breakfast_image);
    const cap = (key) => (lang === "en" ? content[`${key}_caption_en`] : content[`${key}_caption_it`]) || "";

    const waBeach = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(t("whatsapp.beachMessage").replace("{date}", ""))}`;
    const waHotel = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(t("whatsapp.hotelMessage").replace("{date}", "").replace("{people}", ""))}`;

    return (
        <div data-testid="stay-page" className="min-h-screen">
            <SEO
                title={lang === "en" ? "Stay · Villa Smeraldo & RIVA Beach" : "Stay · Villa Smeraldo & RIVA Beach"}
                description={lang === "en"
                    ? "Where to sleep and chill on Monte Argentario for MTB Enduro: Hotel Villa Smeraldo and RIVA Beach. Abnormal discount available."
                    : "Dove dormire e rilassarsi sull'Argentario per MTB Enduro: Hotel Villa Smeraldo e RIVA Beach. Sconto Abnormal."}
                path="/stay"
            />
            {/* HERO */}
            <PageHeader
                label="STAY · ARGENTARIO"
                title={t("stay.title1")}
                accent={t("stay.title2")}
                tagline={["Villa Smeraldo", "RIVA Beach", lang === "en" ? "Abnormal discount" : "Sconto Abnormal"]}
                sub={t("stay.subtitle")}
                image={heroImg}
                imageAlt={cap("hero")}
            />

            {/* HOTEL OVERVIEW */}
            <section data-testid="stay-hotel-section" className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
                <div className="grid lg:grid-cols-2 gap-10 items-start">
                    <div>
                        <p className="text-[11px] tracking-[0.35em] uppercase text-abnormal-acid font-bold flex items-center gap-2">
                            <Anchor size={12} /> {t("stay.hotelEyebrow")}
                        </p>
                        <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.95] mt-3">{t("stay.hotelTitle")}</h2>
                        <p className="text-zinc-300 mt-6 leading-relaxed">{t("stay.hotelSub")}</p>

                        <div className="flex items-start gap-2 text-zinc-400 mt-5 text-sm">
                            <MapPin size={14} className="text-abnormal-acid mt-1 flex-shrink-0" />
                            <span>SP di Giannella 64 · 58015 Orbetello (GR)</span>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href={HOTEL_BOOKING_URL}
                                target="_blank" rel="noreferrer"
                                data-testid="stay-book-cta"
                                className="bg-abnormal-acid text-black uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-abnormal-paper hover:text-black transition-colors text-xs inline-flex items-center gap-2"
                            >
                                {t("stay.ctaBookBtn")} <ExternalLink size={12} />
                            </a>
                            <a
                                href={HOTEL_QUOTE_URL}
                                target="_blank" rel="noreferrer"
                                data-testid="stay-quote-cta"
                                className="border border-zinc-700 text-zinc-200 uppercase tracking-[0.2em] font-bold px-6 py-3 hover:border-abnormal-acid hover:text-abnormal-acid transition-colors text-xs inline-flex items-center gap-2"
                            >
                                {t("stay.ctaQuoteBtn")} <ExternalLink size={12} />
                            </a>
                            <a
                                href={waHotel}
                                target="_blank" rel="noreferrer"
                                data-testid="stay-whatsapp-cta"
                                className="border border-[#25D366] text-[#25D366] uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-[#25D366] hover:text-white transition-colors text-xs inline-flex items-center gap-2"
                            >
                                WhatsApp <MessageSquare size={12} />
                            </a>
                        </div>

                        {showAugustBanner && (
                            <div data-testid="stay-august-banner" className="mt-8 border-2 border-amber-400 bg-amber-400/10 p-4">
                                <p className="text-[10px] tracking-[0.3em] uppercase text-amber-400 font-bold">⚠️ {t("stay.augustBannerTitle")}</p>
                                <p className="text-sm text-zinc-100 mt-1">{t("stay.augustBannerText")}</p>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <figure>
                            <img src={poolImg} alt={cap("pool")} className="w-full h-48 md:h-64 object-cover" />
                            {cap("pool") && <figcaption className="text-[10px] tracking-wider uppercase text-zinc-500 mt-1">{cap("pool")}</figcaption>}
                        </figure>
                        <figure>
                            <img src={breakfastImg} alt={cap("breakfast")} className="w-full h-48 md:h-64 object-cover" />
                            {cap("breakfast") && <figcaption className="text-[10px] tracking-wider uppercase text-zinc-500 mt-1">{cap("breakfast")}</figcaption>}
                        </figure>
                    </div>
                </div>
            </section>

            {/* ROOMS */}
            <section data-testid="stay-rooms-section" className="border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
                    <p className="text-[11px] tracking-[0.35em] uppercase text-abnormal-acid font-bold">{t("stay.roomsTitle")}</p>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <RoomCard name={r.matrimoniale.name} count={r.matrimoniale.count} desc={r.matrimoniale.desc} />
                        <RoomCard name={r.tripla.name} count={r.tripla.count} desc={r.tripla.desc} />
                        <RoomCard name={r.suite.name} count={r.suite.count} desc={r.suite.desc} />
                        <RoomCard name={r.monolocale.name} count={r.monolocale.count} desc={r.monolocale.desc} />
                        <div className="sm:col-span-2 border-2 border-abnormal-acid bg-abnormal-acid/5 p-5 flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="font-display text-xl uppercase leading-tight">{r.appNew.name}</h3>
                                <span className="text-[10px] tracking-[0.25em] uppercase bg-abnormal-acid text-black font-bold px-2 py-1">{r.appNew.count}</span>
                            </div>
                            <p className="text-sm text-zinc-200 leading-relaxed">{r.appNew.desc}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section data-testid="stay-services-section" className="border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
                    <p className="text-[11px] tracking-[0.35em] uppercase text-abnormal-acid font-bold">{t("stay.servicesTitle")}</p>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <ServiceCard icon={Bike} title={svc.bike.title} desc={svc.bike.desc} />
                        <ServiceCard icon={Waves} title={svc.pool.title} desc={svc.pool.desc} />
                        <ServiceCard icon={Coffee} title={svc.breakfast.title} desc={svc.breakfast.desc} />
                        <ServiceCard icon={Sunset} title={svc.beachBar.title} desc={svc.beachBar.desc} />
                        <ServiceCard icon={ParkingSquare} title={svc.parking.title} desc={svc.parking.desc} />
                        <ServiceCard icon={Zap} title={svc.evCharge.title} desc={svc.evCharge.desc} />
                        <ServiceCard icon={Wifi} title={svc.wifi.title} desc={svc.wifi.desc} />
                        <ServiceCard icon={Dumbbell} title={svc.fitness.title} desc={svc.fitness.desc} />
                        <ServiceCard icon={Bike} title={svc.ebike.title} desc={svc.ebike.desc} />
                    </div>
                </div>
            </section>

            {/* PARTNERS */}
            <section data-testid="stay-partners-section" className="border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
                    <p className="text-[11px] tracking-[0.35em] uppercase text-abnormal-acid font-bold">{t("stay.partnersTitle")}</p>
                    <div className="mt-6 grid md:grid-cols-2 gap-3">
                        <a href="https://www.bikepointorbetello.it/" target="_blank" rel="noreferrer" data-testid="partner-bike-point-orbetello-stay"
                            className="group bg-zinc-950 border border-white/10 hover:border-abnormal-acid transition-colors p-6 flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-3">
                                <img src="https://customer-assets.emergentagent.com/job_argentario-enduro/artifacts/b4s4b62f_IMG_0417.jpeg" alt="Bike Point" className="w-16 h-16 object-contain bg-black border border-white/10" />
                                <ExternalLink size={14} className="text-zinc-500 group-hover:text-abnormal-acid" />
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mt-3">{t("stay.partnerBike.title")}</p>
                            <p className="font-display text-2xl uppercase">{t("stay.partnerBike.link")}</p>
                            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{t("stay.partnerBike.desc")}</p>
                        </a>
                        <a href="https://www.tuscanhillenduro.com/" target="_blank" rel="noreferrer" data-testid="partner-tuscan-hill-stay"
                            className="group bg-zinc-950 border border-white/10 hover:border-abnormal-acid transition-colors p-6 flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-3">
                                <img src="https://customer-assets.emergentagent.com/job_argentario-enduro/artifacts/kq5u6iaq_Bazaart_409D2429-99C6-48AA-9ED2-0F5F5333204C.png" alt="Tuscan Hill Enduro" className="w-16 h-16 object-contain bg-black border border-white/10" />
                                <ExternalLink size={14} className="text-zinc-500 group-hover:text-abnormal-acid" />
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mt-3">{t("stay.partnerShuttle.title")}</p>
                            <p className="font-display text-2xl uppercase">{t("stay.partnerShuttle.link")}</p>
                            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{t("stay.partnerShuttle.desc")}</p>
                        </a>
                    </div>
                </div>
            </section>

            {/* BEACH (RIVA) */}
            <section data-testid="stay-beach-section" className="relative overflow-hidden border-t border-white/10">
                <img src={beachImg} alt={cap("beach")} className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-20">
                    <p className="text-[11px] tracking-[0.35em] uppercase text-abnormal-acid font-bold flex items-center gap-2">
                        <Waves size={12} /> {t("stay.riverEyebrow")}
                    </p>
                    <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.95] mt-3">{t("stay.riverTitle")}</h2>
                    <p className="text-zinc-200 max-w-2xl mt-5 leading-relaxed">{t("stay.riverSub")}</p>
                    {cap("beach") && <p className="text-[10px] tracking-wider uppercase text-zinc-400 mt-2">📷 {cap("beach")}</p>}

                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 max-w-3xl">
                        {Object.entries(t("stay.riverFeatures")).map(([k, label]) => (
                            <div key={k} className="border border-zinc-700 bg-black/40 px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-300 font-bold">
                                {label}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 border-2 border-abnormal-acid bg-abnormal-acid/10 p-5 md:p-6 max-w-2xl">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold">⚡ {t("stay.riverPerks")}</p>
                        <p className="text-sm text-zinc-100 mt-2 leading-relaxed">{t("stay.riverPerksText")}</p>
                        <a
                            href={waBeach}
                            target="_blank" rel="noreferrer"
                            data-testid="stay-beach-whatsapp"
                            className="mt-5 inline-flex items-center gap-2 bg-[#25D366] text-white uppercase tracking-[0.2em] font-bold px-5 py-3 hover:bg-white hover:text-[#25D366] transition-colors text-xs"
                        >
                            <MessageSquare size={14} /> {t("stay.riverWhatBtn")}
                        </a>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="border-t border-white/10">
                <div className="max-w-3xl mx-auto px-5 md:px-10 py-20 text-center">
                    <p className="text-[11px] tracking-[0.35em] uppercase text-abnormal-acid font-bold">{t("stay.ctaBookTitle")}</p>
                    <p className="text-zinc-400 mt-3">{t("stay.ctaBookSub")}</p>
                    <a
                        href={HOTEL_BOOKING_URL}
                        target="_blank" rel="noreferrer"
                        data-testid="stay-final-book-cta"
                        className="mt-6 inline-flex items-center gap-2 bg-abnormal-acid text-black uppercase tracking-[0.25em] font-bold px-8 py-4 hover:bg-abnormal-paper hover:text-black transition-colors text-sm"
                    >
                        {t("stay.ctaBookBtn")} <ExternalLink size={14} />
                    </a>
                    <p className="mt-6 text-[10px] tracking-wider uppercase text-zinc-600">
                        <a href={HOTEL_SITE_URL} target="_blank" rel="noreferrer" className="hover:text-abnormal-acid">
                            villasmeraldo.com →
                        </a>
                    </p>
                </div>
            </section>
        </div>
    );
}
